/* ==========================================================================
   Aspire Full Article Template — progressive enhancement
   Nothing here is required for the content to be readable or crawlable:
   the FAQ renders open, the sources render as a list, and the body is intact.
   This file adds the table of contents, the accordion behaviour, and the
   inline citation links on top of that.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-asp-art]');
  if (!root) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ utils */

  function slugify(text, used) {
    var base = String(text)
      .toLowerCase()
      .replace(/['’"“”]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';
    var slug = base;
    var i = 2;
    while (used[slug]) { slug = base + '-' + i; i++; }
    used[slug] = true;
    return slug;
  }

  function scrollToEl(el) {
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* ------------------------------------------------- wrap wide tables safely */

  Array.prototype.forEach.call(root.querySelectorAll('.asp-art-body table'), function (table) {
    if (table.parentNode && table.parentNode.classList.contains('asp-art-tablewrap')) return;
    var wrap = document.createElement('div');
    wrap.className = 'asp-art-tablewrap';
    wrap.setAttribute('tabindex', '0');
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', 'Table, scroll horizontally to see more');
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);
  });

  /* ------------------------------------------------------- FAQ accordion */

  var faqItems = root.querySelectorAll('.asp-art-faq__item');

  Array.prototype.forEach.call(faqItems, function (item, index) {
    var btn = item.querySelector('.asp-art-faq__q');
    var panel = item.querySelector('.asp-art-faq__a');
    if (!btn || !panel) return;

    // Collapse everything on init. Before this runs the answers are visible,
    // which is what a no-JS reader (and any crawler) sees.
    var open = false;
    panel.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    item.setAttribute('data-open', 'false');

    function setOpen(next) {
      open = next;
      panel.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      item.setAttribute('data-open', open ? 'true' : 'false');
    }

    btn.addEventListener('click', function () { setOpen(!open); });

    // Deep-linking straight to a question opens it.
    if (window.location.hash && window.location.hash.slice(1) === item.id) {
      setOpen(true);
    }
    void index;
  });

  /* ------------------------------------------------- collapsible sources panel */

  var sourcesSection = root.querySelector('[data-asp-sources]');
  var sourcesToggle = sourcesSection && sourcesSection.querySelector('.asp-art-sources__toggle');
  var sourcesPanel = sourcesSection && sourcesSection.querySelector('.asp-art-sources__body');

  function setSourcesOpen(open) {
    if (!sourcesToggle || !sourcesPanel) return;
    sourcesPanel.hidden = !open;
    sourcesToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  if (sourcesToggle && sourcesPanel) {
    // Rendered open so no-JS readers and crawlers get the list; collapse here.
    if (sourcesSection.getAttribute('data-collapsed') === 'true') {
      setSourcesOpen(false);
    }
    sourcesToggle.addEventListener('click', function () {
      setSourcesOpen(sourcesToggle.getAttribute('aria-expanded') !== 'true');
    });
  }

  /* ------------------------------- inline [n] citations -> superscript links */

  var refList = root.querySelector('.asp-art-sources__list');

  if (refList) {
    var refIds = {};
    var citeCounter = 0;
    Array.prototype.forEach.call(refList.children, function (li) {
      if (li.id) refIds[li.getAttribute('data-ref-number')] = li.id;
    });

    var citeScopes = root.querySelectorAll('.asp-art-body, .asp-art-faq__a');

    Array.prototype.forEach.call(citeScopes, function (scope) {
      var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          if (!/\[\d{1,3}(\s*,\s*\d{1,3})*\]/.test(node.nodeValue)) {
            return NodeFilter.FILTER_REJECT;
          }
          // Never rewrite inside links or code — that would nest anchors.
          var p = node.parentNode;
          while (p && p !== scope) {
            var tag = p.nodeName;
            if (tag === 'A' || tag === 'CODE' || tag === 'PRE' || tag === 'SUP') {
              return NodeFilter.FILTER_REJECT;
            }
            p = p.parentNode;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      var targets = [];
      var current;
      while ((current = walker.nextNode())) targets.push(current);

      targets.forEach(function (node) {
        var frag = document.createDocumentFragment();
        var text = node.nodeValue;
        var re = /\[(\d{1,3}(?:\s*,\s*\d{1,3})*)\]/g;
        var last = 0;
        var match;
        var replacedAny = false;

        while ((match = re.exec(text))) {
          var numbers = match[1].split(',').map(function (n) { return n.trim(); });
          var known = numbers.filter(function (n) { return refIds[n]; });
          if (!known.length) continue;

          frag.appendChild(document.createTextNode(text.slice(last, match.index)));

          var sup = document.createElement('sup');
          sup.className = 'asp-art-cite';
          numbers.forEach(function (n, i) {
            if (i) sup.appendChild(document.createTextNode(','));
            if (!refIds[n]) {
              sup.appendChild(document.createTextNode(n));
              return;
            }
            var a = document.createElement('a');
            var citeId = 'asp-cite-' + (++citeCounter);
            a.href = '#' + refIds[n];
            a.id = citeId;
            a.textContent = n;
            a.setAttribute('aria-label', 'Jump to reference ' + n);
            // Clicking a citation teaches that reference's back-link where to
            // return to, so the reader lands back mid-sentence.
            a.addEventListener('click', function (event) {
              // A collapsed sources panel would otherwise swallow the jump.
              setSourcesOpen(true);
              var li = document.getElementById(refIds[n]);
              if (!li) return;
              var back = li.querySelector('[data-asp-backref]');
              if (back) back.setAttribute('href', '#' + citeId);
              event.preventDefault();
              scrollToEl(li);
              if (history.replaceState) history.replaceState(null, '', '#' + refIds[n]);
            });
            sup.appendChild(a);
          });
          frag.appendChild(sup);

          last = match.index + match[0].length;
          replacedAny = true;
        }

        if (!replacedAny) return;
        frag.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
      });
    });
  }

  /* ----------------------------------------------------- table of contents */

  var body = root.querySelector('.asp-art-body');
  var tocMounts = root.querySelectorAll('[data-asp-toc]');

  if (body && tocMounts.length) {
    var used = {};

    // Existing ids win, so hand-authored anchors and inbound links keep working.
    Array.prototype.forEach.call(root.querySelectorAll('[id]'), function (el) {
      used[el.id] = true;
    });

    // Body headings, plus the FAQ/Sources headings we render ourselves. The
    // combined selector returns nodes in document order.
    var headings = root.querySelectorAll(
      '.asp-art-body h2, .asp-art-body h3, .asp-art-body h1, .asp-art-toc-anchor'
    );

    var entries = [];
    Array.prototype.forEach.call(headings, function (h) {
      var label = (h.textContent || '').replace(/\s+/g, ' ').trim();
      if (!label) return;
      if (!h.id) h.id = slugify(label, used);
      // h1 in a legacy body is styled and treated as a section heading.
      var level = h.classList.contains('asp-art-toc-anchor')
        ? 2
        : (h.nodeName === 'H3' ? 3 : 2);
      entries.push({ el: h, id: h.id, label: label, level: level });
    });

    if (entries.length >= 2) {
      var allLinks = [];

      Array.prototype.forEach.call(tocMounts, function (mount) {
        var list = document.createElement('ol');

        entries.forEach(function (entry) {
          var li = document.createElement('li');
          li.setAttribute('data-level', String(entry.level));

          var a = document.createElement('a');
          a.href = '#' + entry.id;
          a.textContent = entry.label;
          a.addEventListener('click', function (event) {
            event.preventDefault();
            if (sourcesSection && sourcesSection.contains(entry.el)) setSourcesOpen(true);
            scrollToEl(entry.el);
            if (history.replaceState) {
              history.replaceState(null, '', '#' + entry.id);
            }
            // On mobile the TOC is a sticky bar; collapse it after a jump.
            var panel = mount.closest('.asp-art-toc');
            if (panel && panel.classList.contains('asp-art-toc--bar')) {
              var head = panel.querySelector('.asp-art-toc__head');
              if (head) head.setAttribute('aria-expanded', 'false');
            }
          });

          li.appendChild(a);
          list.appendChild(li);
          allLinks.push({ link: a, id: entry.id });
        });

        mount.innerHTML = '';
        mount.appendChild(list);

        var container = mount.closest('.asp-art-toc');
        if (container) container.classList.add('is-ready');
      });

      // Both the desktop rail and the mobile card start expanded — the contents
      // list is the point of the block, and on mobile it is no longer a pinned
      // bar competing for screen space.
      Array.prototype.forEach.call(root.querySelectorAll('.asp-art-toc__head'), function (head) {
        head.setAttribute('aria-expanded', 'true');
        head.addEventListener('click', function () {
          var isOpen = head.getAttribute('aria-expanded') === 'true';
          head.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        });
      });

      // Scroll-spy: highlight the last heading whose top has passed the offset.
      var offset = 96;
      var ticking = false;

      function syncActive() {
        ticking = false;
        var activeId = entries[0].id;
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].el.getBoundingClientRect().top - offset <= 0) {
            activeId = entries[i].id;
          } else {
            break;
          }
        }
        // At the very bottom of the page, favour the final entry.
        if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 4) {
          activeId = entries[entries.length - 1].id;
        }
        allLinks.forEach(function (item) {
          if (item.id === activeId) {
            item.link.setAttribute('aria-current', 'true');
          } else {
            item.link.removeAttribute('aria-current');
          }
        });
      }

      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(syncActive);
      }, { passive: true });

      window.addEventListener('resize', syncActive, { passive: true });
      syncActive();
    }
  }


  /* ------------------------------------------------------- reading progress

     Measured across the article body only, not the whole document, so the bar
     reaches 100% when the reader finishes the article rather than when they
     reach the bottom of the footer. */

  var progress = root.querySelector('[data-asp-progress]');
  var progressFill = progress && progress.querySelector('.asp-art-progress__fill');
  var progressTarget = root.querySelector('.asp-art-main') || root;

  if (progress && progressFill) {
    var progressTicking = false;

    function drawProgress() {
      progressTicking = false;

      var box = progressTarget.getBoundingClientRect();
      var start = box.top + (window.scrollY || window.pageYOffset);
      var end = start + box.height - window.innerHeight;
      var span = end - start;

      if (span <= 0) {            // article shorter than the viewport
        progress.classList.remove('is-ready');
        return;
      }

      progress.classList.add('is-ready');
      var pct = ((window.scrollY || window.pageYOffset) - start) / span;
      pct = Math.max(0, Math.min(1, pct));
      progressFill.style.width = (pct * 100).toFixed(2) + '%';
    }

    window.addEventListener('scroll', function () {
      if (progressTicking) return;
      progressTicking = true;
      window.requestAnimationFrame(drawProgress);
    }, { passive: true });

    window.addEventListener('resize', drawProgress, { passive: true });
    drawProgress();
  }
})();
